import {
  Box,
  Card,
  CardBody,
  Flex,
  Heading,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { addDays, format, getDay } from "date-fns";
import { TZDate } from "@date-fns/tz";
import AppPage from "layouts/AppPage";
import useCaminoStore from "stores/caminoStore";
import CaminoBreadcrumbs from "components/Breadcrumbs";
import { SHARED_ROUTES } from "definitions/constants/routeConstants";
import { useJourneyPreviewMutation } from "../hooks";
import PreviewControls from "./PreviewControls";
import PreviewListView from "./PreviewListView";
import PreviewMonthView from "./PreviewMonthView";
import PreviewWeekView from "./PreviewWeekView";

const DEFAULT_TIMEZONE = "America/Los_Angeles";

function defaultStartDate(timezone: string): string {
  const today = new TZDate(new Date(), timezone);
  // Default to the next Monday so the calendar lands on a typical onboarding day.
  // If today is already Monday, advance a full week so we don't default to today.
  const daysUntilMonday = (8 - getDay(today)) % 7 || 7;
  return format(addDays(today, daysUntilMonday), "yyyy-MM-dd");
}

export default function JourneyPreviewPage() {
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const currentCompany = useCaminoStore((state) => state.currentCompany);
  const timezone = currentCompany?.defaultTimezone || DEFAULT_TIMEZONE;

  const initialPathIds = useMemo(() => {
    const raw = searchParams.get("pathIds");
    return raw ? raw.split(",").filter(Boolean) : [];
  }, [searchParams]);

  const [selectedPathIds, setSelectedPathIds] = useState<string[]>(initialPathIds);
  const [startDate, setStartDate] = useState<string>(() => defaultStartDate(timezone));
  const previewMutation = useJourneyPreviewMutation();

  const runPreview = useCallback(() => {
    previewMutation.mutate(
      { pathIds: selectedPathIds, startDate, timezone },
      {
        onError: (error) => {
          toast({
            title: "Couldn't generate preview",
            description: error?.message || "Please try again.",
            status: "error",
            duration: 5000,
          });
        },
      },
    );
  }, [previewMutation, selectedPathIds, startDate, timezone, toast]);

  // Auto-run when launched from the path detail page with pre-selected paths
  useEffect(() => {
    if (initialPathIds.length > 0) {
      runPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const preview = previewMutation.data;

  return (
    <AppPage>
      <CaminoBreadcrumbs
        breadcrumbInfos={[{ name: "Paths", toUrl: SHARED_ROUTES.paths.index }, { name: "Journey preview" }]}
      />
      <Stack spacing="6" mt="6">
        <Box>
          <Heading size="md">Journey preview</Heading>
          <Text fontSize="sm" color="gray.600" mt="1">
            Select one or more paths and a hypothetical start date to see what a new hire&apos;s onboarding would look
            like. Nothing is saved — this is a read-only projection.
          </Text>
        </Box>

        <PreviewControls
          selectedPathIds={selectedPathIds}
          onPathIdsChange={setSelectedPathIds}
          startDate={startDate}
          onStartDateChange={setStartDate}
          onRunPreview={runPreview}
          isLoading={previewMutation.isPending}
        />

        {preview && (
          <Card>
            <CardBody>
              <Flex justify="space-between" align="center" mb="4">
                <Box>
                  <Text fontSize="sm" color="gray.600">
                    Starting
                  </Text>
                  <Text fontWeight="semibold">
                    {preview.startDate} · {preview.timezone}
                  </Text>
                </Box>
                <Text fontSize="sm" color="gray.500">
                  {preview.actions.length} actions across {preview.paths.length} path
                  {preview.paths.length === 1 ? "" : "s"}
                </Text>
              </Flex>

              <Tabs variant="line" colorScheme="blue">
                <TabList>
                  <Tab>List</Tab>
                  <Tab>Week</Tab>
                  <Tab>Month</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel px="0">
                    <PreviewListView
                      actions={preview.actions}
                      startDate={preview.startDate}
                      timezone={preview.timezone}
                    />
                  </TabPanel>
                  <TabPanel px="0">
                    <PreviewWeekView
                      actions={preview.actions}
                      startDate={preview.startDate}
                      timezone={preview.timezone}
                    />
                  </TabPanel>
                  <TabPanel px="0">
                    <PreviewMonthView
                      actions={preview.actions}
                      startDate={preview.startDate}
                      timezone={preview.timezone}
                    />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </CardBody>
          </Card>
        )}
      </Stack>
    </AppPage>
  );
}
